/**
 * CameraMarker
 *
 * Map marker component for displaying a VMS camera location on the dispatch map.
 * Color-coded based on status, with alert badge showing count.
 *
 * Popup state is controlled by parent to ensure only one popup is open at a time.
 */

import React, { memo } from 'react';
import { Box, Paper, Typography, Button, ClickAwayListener } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import styles from './CameraMarker.module.css';
import classNames from 'classnames';

/** Camera status based on connectivity */
export type CameraStatus = 'online' | 'offline' | 'unknown';

/** Camera data for marker display */
export interface Camera {
  id: number;
  name: string;
  display_name?: string;
  latitude: number;
  longitude: number;
  status: CameraStatus;
  site_id: number;
  site_name: string;
  has_alerts?: boolean;
  alert_count?: number;
}

interface CameraMarkerProps {
  /** Camera data */
  camera: Camera;
  /** Whether this marker is currently selected */
  isSelected?: boolean;
  /** Whether the popup should be shown (controlled by parent) */
  showPopup?: boolean;
  /** Callback when marker is clicked */
  onClick?: (camera: Camera) => void;
  /** Callback when "View Feed" is clicked */
  onViewFeed?: (camera: Camera) => void;
  /** Callback to close the popup (click away) */
  onClosePopup?: () => void;
  /** Required by google-map-react for positioning */
  lat: number;
  /** Required by google-map-react for positioning */
  lng: number;
}

/**
 * Status color mapping
 * NOTE: These are used as inline styles for google-map-react markers which
 * render outside the MUI ThemeProvider context, so resolved hex values are required here.
 * The values correspond to MUI default palette tokens:
 *   online  -> success.main
 *   offline -> grey[500]
 *   unknown -> warning.main
 */
export const CAMERA_STATUS_COLORS: Record<CameraStatus, string> = {
  online: '#4CAF50',
  offline: '#9E9E9E',
  unknown: '#FFC107',
};

/** Status display labels */
export const CAMERA_STATUS_LABELS: Record<CameraStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  unknown: 'Unknown',
};

const CameraMarker: React.FC<CameraMarkerProps> = ({
  camera,
  isSelected = false,
  showPopup = false,
  onClick,
  onViewFeed,
  onClosePopup,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(camera);
  };

  const handleViewFeed = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewFeed?.(camera);
  };

  const handleClickAway = () => {
    if (showPopup) {
      onClosePopup?.();
    }
  };

  const hasActiveIncidents = (camera.alert_count ?? 0) > 0;
  // error.main equivalent for map markers rendered outside ThemeProvider
  const statusColor = hasActiveIncidents ? '#d32f2f' : CAMERA_STATUS_COLORS[camera.status];
  const displayName = camera.display_name || camera.name;

  // Map status to CSS class names for animations
  const statusClassName = {
    online: styles.statusOnline,
    offline: styles.statusOffline,
    unknown: styles.statusUnknown,
  }[camera.status];

  const markerContent = (
    <div
      className={classNames(styles.markerContainer, {
        [styles.selected]: isSelected,
      })}
    >
      {/* Marker Pin */}
      <div
        className={classNames(styles.markerPin, {
          [statusClassName]: !hasActiveIncidents,
          [styles.hasIncident]: hasActiveIncidents,
          [styles.selected]: isSelected,
        })}
        style={{ backgroundColor: statusColor, color: statusColor }}
        onClick={handleClick}
        title={hasActiveIncidents ? `${displayName} — ${camera.alert_count} incident${camera.alert_count !== 1 ? 's' : ''}` : displayName}
      >
        <VideocamIcon className={styles.markerIcon} />

        {/* Alert Badge */}
        {hasActiveIncidents && (
          <div className={styles.alertBadge}>
            {camera.alert_count > 9 ? '9+' : camera.alert_count}
          </div>
        )}
      </div>

      {/* Popup */}
      {showPopup && (
        <Paper className={styles.popup} elevation={4}>
          {/* Header */}
          <Box className={styles.popupHeader}>
            <Box
              className={styles.popupStatusDot}
              style={{ backgroundColor: statusColor }}
            />
            <Typography variant="subtitle1" className={styles.popupName}>
              {displayName}
            </Typography>
          </Box>

          {/* Status Row */}
          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">
              Status:
            </Typography>
            <Typography
              variant="body2"
              className={classNames(styles.popupValue, styles[`status_${camera.status}`])}
            >
              {CAMERA_STATUS_LABELS[camera.status]}
            </Typography>
          </Box>

          {/* Site Row */}
          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">
              Site:
            </Typography>
            <Typography variant="body2" className={styles.popupValue}>
              {camera.site_name}
            </Typography>
          </Box>

          {/* Alert Row */}
          {hasActiveIncidents && (
            <Box className={styles.popupRow}>
              <Typography variant="body2" color="textSecondary">
                Alerts:
              </Typography>
              <Box className={styles.alertDisplay}>
                <NotificationsActiveIcon className={styles.alertIcon} />
                <Typography variant="body2" className={styles.alertText}>
                  {camera.alert_count} active
                </Typography>
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box className={styles.popupActions}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleViewFeed}
            >
              View Feed
            </Button>
          </Box>
        </Paper>
      )}
    </div>
  );

  // Wrap with ClickAwayListener only when popup is shown
  if (showPopup) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        {markerContent}
      </ClickAwayListener>
    );
  }

  return markerContent;
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the marker change
 */
function arePropsEqual(
  prevProps: CameraMarkerProps,
  nextProps: CameraMarkerProps
): boolean {
  // Compare UI state
  if (
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.showPopup !== nextProps.showPopup
  ) {
    return false;
  }

  // Compare camera data by relevant values (not reference)
  const prevCamera = prevProps.camera;
  const nextCamera = nextProps.camera;

  if (
    prevCamera.id !== nextCamera.id ||
    prevCamera.name !== nextCamera.name ||
    prevCamera.display_name !== nextCamera.display_name ||
    prevCamera.status !== nextCamera.status ||
    prevCamera.latitude !== nextCamera.latitude ||
    prevCamera.longitude !== nextCamera.longitude ||
    prevCamera.site_name !== nextCamera.site_name ||
    prevCamera.has_alerts !== nextCamera.has_alerts ||
    prevCamera.alert_count !== nextCamera.alert_count
  ) {
    return false;
  }

  return true;
}

export default memo(CameraMarker, arePropsEqual);
